import { useEffect, useRef, useState } from 'react';
import type { Choice } from '@pons/contracts';
import type { CoinView } from './model';
import type { SimRound } from '../demo/simulation';
type Simulation = typeof import('../demo/simulation');
// The simulated market is invented data, so its code loads only when asked for.
export function useSimulation(enabled: boolean, trading: boolean, now: number) {
  const [simulation, setSimulation] = useState<Simulation | null>(null);
  const [rounds, setRounds] = useState<SimRound[]>([]);
  const [calls, setCalls] = useState<Record<string, Choice>>({});
  const [paused, setPaused] = useState(false);
  const serial = useRef(0);
  const seeded = useRef(false);
  const nowRef = useRef(now);
  nowRef.current = now;
  useEffect(() => {
    if (!enabled || simulation) return;
    let active = true;
    void import('../demo/simulation').then((loaded) => {
      if (active) setSimulation(loaded);
    });
    return () => {
      active = false;
    };
  }, [enabled, simulation]);
  useEffect(() => {
    if (enabled) return;
    seeded.current = false;
    setRounds([]);
    setCalls({});
    setPaused(false);
  }, [enabled]);
  useEffect(() => {
    if (!enabled || !simulation || seeded.current) return;
    const seed = simulation.simulatedRounds(now);
    serial.current = seed.length;
    setRounds(seed);
    setPaused(false);
    seeded.current = true;
  }, [enabled, simulation, now]);
  useEffect(() => {
    if (!enabled || !simulation || paused || !rounds[0]) return;
    if (now - rounds[0].start < simulation.SIM_STEP_MS) return;
    const next = serial.current++;
    setRounds((list) =>
      [
        { ...simulation.makeSimRound(next, now), id: 'sim-' + next },
        ...list,
      ].slice(0, 36),
    );
  }, [enabled, simulation, paused, now, rounds]);
  const coins: CoinView[] =
    enabled && simulation
      ? rounds.map((round) =>
          simulation.toSimulatedCoin(
            { ...round, choice: calls[round.id] },
            now,
            trading,
          ),
        )
      : [];
  return {
    coins,
    art: (coin: CoinView, className?: string) =>
      simulation ? (
        <simulation.SimArt symbol={coin.symbol} className={className} />
      ) : null,
    call: (id: string, choice: Choice) =>
      setCalls((current) => {
        const round = rounds.find((item) => item.id === id);
        if (
          !enabled ||
          !simulation ||
          !round ||
          current[id] ||
          !simulation.canSimulatePrediction(round, nowRef.current)
        )
          return current;
        return { ...current, [id]: choice };
      }),
    create: (name?: string, symbol?: string) => {
      if (!enabled || !simulation) return null;
      const next = serial.current++;
      const round = simulation.createSimRound(
        next,
        nowRef.current,
        name,
        symbol,
      );
      setRounds((list) => [round, ...list]);
      return round.id;
    },
    clear: () => {
      setRounds([]);
      setCalls({});
      setPaused(true);
    },
  };
}
